import { AnalyticsApiTypes } from "@stellar/analytics-api-client";
import { DateTimeUtils } from "@stellar/utils";

export type DailyMetricDataPoint = {
  /** Formatted calendar label shown on the X-axis (e.g. "Jun 1"). */
  label: string;
  value: number;
};

/** Number of characters in an ISO date-only string (YYYY-MM-DD). */
const ISO_DATE_LENGTH = 10;

/** Minimum proportion of tickStep gap needed to also render the final label. */
const X_TICK_LAST_LABEL_MIN_GAP_RATIO = 0.6;

/**
 * Formats a Date as an ISO date-only string in UTC (YYYY-MM-DD).
 *
 * Note: output uses UTC because it relies on Date.toISOString().
 */
export function formatDateOnly(date: Date): string {
  return date.toISOString().slice(0, ISO_DATE_LENGTH);
}

/** Returns the selected metric value for a project item, defaulting to 0 when missing. */
function getMetricValue(value: number | undefined): number {
  return value ?? 0;
}

/** Formats a Date as a short month+day label (e.g. "Jun 1"). */
function formatDayLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Creates a zero-initialized dataset with one entry per day in the selected range. */
function createEmptyDailyMetricDataset(
  totalDays: number,
  startDate: Date
): DailyMetricDataPoint[] {
  return Array.from({ length: totalDays }, (_, index) => ({
    label: formatDayLabel(
      new Date(startDate.getTime() + index * DateTimeUtils.MILLISECONDS_IN_DAY)
    ),
    value: 0,
  }));
}

/**
 * Maps project metrics from Analytics API to chart-friendly daily points for a selected metric.
 */
export function buildDailyMetricDataset({
  projectMetrics,
  startDate,
  totalDays,
  metricKey,
}: {
  projectMetrics: AnalyticsApiTypes.GetProjectMetricsRoute["response"] | null;
  startDate: Date;
  totalDays: number;
  metricKey: AnalyticsApiTypes.MetricKey;
}): DailyMetricDataPoint[] {
  const byDay = createEmptyDailyMetricDataset(totalDays, startDate);

  if (!projectMetrics) {
    return byDay;
  }

  const fromTimestamp = DateTimeUtils.getDayEdge({
    date: projectMetrics.from,
    edge: "start",
    shouldUseUtc: true,
  }).getTime();

  let pointsInRange = 0;

  projectMetrics.items.forEach((item) => {
    const createdTimestamp = DateTimeUtils.getDayEdge({
      date: item.createdDate,
      edge: "start",
      shouldUseUtc: true,
    }).getTime();
    const dayIndex = Math.floor(
      (createdTimestamp - fromTimestamp) / DateTimeUtils.MILLISECONDS_IN_DAY
    );

    if (dayIndex < 0 || dayIndex >= totalDays) {
      return;
    }

    const metricValue = getMetricValue(item.metrics[metricKey]);
    byDay[dayIndex].value += metricValue;
    pointsInRange += metricValue;
  });

  if (totalDays > 0 && pointsInRange === 0 && projectMetrics.items.length > 0) {
    projectMetrics.items.forEach((item, index) => {
      const dayIndex = Math.min(
        Math.floor((index * totalDays) / projectMetrics.items.length),
        totalDays - 1
      );
      byDay[dayIndex].value += getMetricValue(item.metrics[metricKey]);
    });
  }

  return byDay;
}

/**
 * Decides whether a date label should be rendered on the X-axis for a given tick index.
 *
 * Uses the computed `tickStep` to skip intermediate labels and, for the final day,
 * renders it only when there is enough gap from the previous rendered step label.
 */
export function shouldRenderXTickLabel(
  index: number,
  totalItems: number,
  tickStep: number
): boolean {
  if (totalItems <= 1) {
    return true;
  }

  const lastIndex = totalItems - 1;
  const isStepTick = index % tickStep === 0;

  if (index !== lastIndex) {
    return isStepTick;
  }

  if (isStepTick) {
    return true;
  }

  const previousStepTick = Math.floor(lastIndex / tickStep) * tickStep;
  const minGapToRenderLastTick = Math.ceil(
    tickStep * X_TICK_LAST_LABEL_MIN_GAP_RATIO
  );

  return lastIndex - previousStepTick >= minGapToRenderLastTick;
}
